'use client';
import React, { useState } from 'react';
import { evaluateWellness, STATUS_META } from '@/lib/wellness';
import type { WellnessInput } from '@/lib/wellness';
import { submitWellnessCheckin } from '@/lib/wellnessService';
import {
  Moon, Zap, Brain, Heart, Utensils, Droplets,
  AlertTriangle, Activity, MessageSquare, CheckCircle2,
  ChevronRight, Loader2
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface WellnessCheckinFormProps {
  athleteId: string;
  userId: string | null;
  onComplete?: (score: number, status: string) => void;
}

const SCALE_LABELS: Record<number, string> = {
  1: '1 — Very Poor',
  2: '2 — Poor',
  3: '3 — Moderate',
  4: '4 — Good',
  5: '5 — Excellent',
};

function ScaleInput({
  label,
  icon: Icon,
  value,
  onChange,
  inverseLabel,
}: {
  label: string;
  icon: React.ElementType;
  value: number;
  onChange: (v: number) => void;
  inverseLabel?: boolean;
}) {
  const colors = inverseLabel
    ? ['bg-emerald-500', 'bg-emerald-400', 'bg-amber-400', 'bg-orange-400', 'bg-red-500']
    : ['bg-red-500', 'bg-orange-400', 'bg-amber-400', 'bg-emerald-400', 'bg-emerald-500'];

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon size={15} className="text-zinc-400 flex-shrink-0" />
        <span className="text-sm font-medium text-zinc-300">{label}</span>
        <span className="ml-auto text-xs text-zinc-500">{SCALE_LABELS[value]}</span>
      </div>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className={`flex-1 h-9 rounded-lg text-sm font-semibold transition-all duration-150 border ${
              value === v
                ? `${colors[v - 1]} border-transparent text-white shadow-lg scale-105`
                : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:border-zinc-600 hover:text-zinc-200'
            }`}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

const DEFAULT_INPUT: WellnessInput = {
  sleepQuality: 3,
  sleepDuration: 7,
  sorenessLevel: 3,
  stressLevel: 3,
  energyLevel: 3,
  mood: 3,
  nutritionQuality: 3,
  hydrationQuality: 3,
  bodyPain: false,
  painLocation: '',
  practiceYesterday: false,
  practiceToday: false,
  notesToCoach: '',
};

export default function WellnessCheckinForm({ athleteId, userId, onComplete }: WellnessCheckinFormProps) {
  const [input, setInput] = useState<WellnessInput>(DEFAULT_INPUT);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preview = evaluateWellness(input);
  const meta = STATUS_META[preview.status];

  const set = <K extends keyof WellnessInput>(key: K, val: WellnessInput[K]) =>
    setInput(prev => ({ ...prev, [key]: val }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    const { data, error: err } = await submitWellnessCheckin(athleteId, userId, input);
    setSubmitting(false);

    if (err) {
      setError(err);
      return;
    }

    setSubmitted(true);
    onComplete?.(preview.score, preview.status);
  };

  if (submitted) {
    return (
      <div className="card p-8 flex flex-col items-center text-center gap-4">
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center border-2"
          style={{ background: meta.bg, borderColor: meta.border }}
        >
          <CheckCircle2 size={36} style={{ color: meta.color }} />
        </div>
        <div>
          <p className="text-xl font-bold text-zinc-100">Check-In Complete</p>
          <p className="text-sm text-zinc-500 mt-1">Your readiness has been recorded for today</p>
        </div>
        <div
          className="px-6 py-3 rounded-xl border"
          style={{ background: meta.bg, borderColor: meta.border }}
        >
          <p className="text-4xl font-black" style={{ color: meta.color }}>{preview.score}</p>
          <p className="text-sm font-semibold mt-1" style={{ color: meta.color }}>{meta.label}</p>
        </div>
        <p className="text-sm text-zinc-400 max-w-sm">{preview.trainingAdjustment}</p>
        <button
          type="button"
          onClick={() => { setSubmitted(false); setInput(DEFAULT_INPUT); }}
          className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors mt-2"
        >
          Submit another check-in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Live Score Preview */}
      <div
        className="rounded-xl border p-4 flex items-center justify-between"
        style={{ background: meta.bg, borderColor: meta.border }}
      >
        <div>
          <p className="text-xs font-medium uppercase tracking-widest" style={{ color: meta.color }}>
            Live Readiness Preview
          </p>
          <p className="text-sm text-zinc-400 mt-0.5">{preview.interpretation}</p>
        </div>
        <div className="text-right flex-shrink-0 ml-4">
          <p className="text-4xl font-black" style={{ color: meta.color }}>{preview.score}</p>
          <p className="text-xs font-semibold" style={{ color: meta.color }}>{meta.label}</p>
        </div>
      </div>

      {/* Sleep */}
      <div className="card p-5 space-y-4">
        <p className="text-xs font-medium uppercase tracking-widest text-zinc-500">Sleep</p>
        <ScaleInput label="Sleep Quality" icon={Moon} value={input.sleepQuality} onChange={v => set('sleepQuality', v)} />
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Moon size={15} className="text-zinc-400" />
            <span className="text-sm font-medium text-zinc-300">Sleep Duration</span>
            <span className="ml-auto text-xs text-zinc-500">{input.sleepDuration} hrs</span>
          </div>
          <input
            type="range"
            min={3}
            max={12}
            step={0.5}
            value={input.sleepDuration}
            onChange={e => set('sleepDuration', parseFloat(e.target.value))}
            className="w-full"
            style={{ accentColor: '#E10600' }}
          />
          <div className="flex justify-between text-xs text-zinc-600">
            <span>3 hrs</span><span>7.5 hrs</span><span>12 hrs</span>
          </div>
        </div>
      </div>

      {/* Physical */}
      <div className="card p-5 space-y-4">
        <p className="text-xs font-medium uppercase tracking-widest text-zinc-500">Physical State</p>
        <ScaleInput label="Soreness Level" icon={Activity} value={input.sorenessLevel} onChange={v => set('sorenessLevel', v)} inverseLabel />
        <ScaleInput label="Energy Level" icon={Zap} value={input.energyLevel} onChange={v => set('energyLevel', v)} />
      </div>

      {/* Mental */}
      <div className="card p-5 space-y-4">
        <p className="text-xs font-medium uppercase tracking-widest text-zinc-500">Mental State</p>
        <ScaleInput label="Stress Level" icon={Brain} value={input.stressLevel} onChange={v => set('stressLevel', v)} inverseLabel />
        <ScaleInput label="Mood" icon={Heart} value={input.mood} onChange={v => set('mood', v)} />
      </div>

      {/* Nutrition & Hydration */}
      <div className="card p-5 space-y-4">
        <p className="text-xs font-medium uppercase tracking-widest text-zinc-500">Nutrition & Hydration</p>
        <ScaleInput label="Nutrition Quality" icon={Utensils} value={input.nutritionQuality} onChange={v => set('nutritionQuality', v)} />
        <ScaleInput label="Hydration Quality" icon={Droplets} value={input.hydrationQuality} onChange={v => set('hydrationQuality', v)} />
      </div>

      {/* Flags */}
      <div className="card p-5 space-y-4">
        <p className="text-xs font-medium uppercase tracking-widest text-zinc-500">Activity & Pain Flags</p>

        {/* Pain */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={15} className="text-zinc-400" />
              <span className="text-sm font-medium text-zinc-300">Body Pain</span>
            </div>
            <button
              type="button"
              onClick={() => set('bodyPain', !input.bodyPain)}
              className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${input.bodyPain ? 'bg-red-500' : 'bg-zinc-700'}`}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200 ${input.bodyPain ? 'translate-x-5' : 'translate-x-0'}`}
              />
            </button>
          </div>
          {input.bodyPain && (
            <div>
              <input
                type="text"
                placeholder="Pain location (e.g. left hamstring, right knee)"
                value={input.painLocation ?? ''}
                onChange={e => set('painLocation', e.target.value)}
                className="input-field w-full"
              />
              <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                <AlertTriangle size={11} /> Pain flag will mark you as Red — coach will be alerted
              </p>
            </div>
          )}
        </div>

        {/* Practice flags */}
        {[
          { key: 'practiceYesterday' as const, label: 'Practice / Game Yesterday' },
          { key: 'practiceToday' as const, label: 'Practice / Game Today' },
        ].map(({ key, label }) => (
          <div key={key} className="flex items-center justify-between">
            <span className="text-sm font-medium text-zinc-300">{label}</span>
            <button
              type="button"
              onClick={() => set(key, !input[key])}
              className={`relative w-11 h-6 rounded-full transition-colors duration-200`}
              style={{ background: input[key] ? '#E10600' : 'rgba(255,255,255,0.08)' }}
            >
              <span
                className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-200 ${input[key] ? 'translate-x-5' : 'translate-x-0'}`}
              />
            </button>
          </div>
        ))}
      </div>

      {/* Notes */}
      <div className="card p-5 space-y-3">
        <div className="flex items-center gap-2">
          <MessageSquare size={15} className="text-zinc-400" />
          <p className="text-xs font-medium uppercase tracking-widest text-zinc-500">Notes to Coach</p>
        </div>
        <textarea
          rows={3}
          placeholder="Anything the coach should know about today..."
          value={input.notesToCoach ?? ''}
          onChange={e => set('notesToCoach', e.target.value)}
          className="input-field w-full resize-none"
        />
      </div>

      {/* Training Recommendation Preview */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">
        <p className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">Training Recommendation</p>
        <p className="text-sm text-zinc-300">{preview.trainingAdjustment}</p>
        <div className="flex flex-wrap gap-2 mt-3">
          {preview.suggestedFocus.map((f, i) => (
            <span key={i} className="px-2.5 py-1 rounded-lg bg-zinc-800 border border-zinc-700 text-xs text-zinc-400">
              {f}
            </span>
          ))}
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/20 rounded-lg px-4 py-3">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="btn-primary w-full flex items-center justify-center gap-2 py-3"
      >
        {submitting ? (
          <><Loader2 size={16} className="animate-spin" /> Submitting...</>
        ) : (
          <>Submit Check-In <ChevronRight size={16} /></>
        )}
      </button>
    </form>
  );
}
